import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/KeycloakContext';
import './EmailComposerModal.css';

const EmailComposerModal = ({ isOpen, onClose, selectedProspects, onSent }) => {
  const { isPersonalWorkspace, isOrganizationWorkspace } = useWorkspace();
  const { keycloak } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Determine if logo should be shown
  const isSocialMember = !!(
    keycloak?.tokenParsed?.identity_provider ||
    keycloak?.tokenParsed?.idp ||
    keycloak?.tokenParsed?.iss?.includes('google')
  );

  const showLogoSection = isOrganizationWorkspace && !isSocialMember && !isPersonalWorkspace;

  useEffect(() => {
    if (isOpen) {
      if (keycloak?.tokenParsed) {
        setSenderName(keycloak.tokenParsed.name || '');
        setSenderEmail(keycloak.tokenParsed.email || '');
      }
      fetchInitialData();
    }
  }, [isOpen, keycloak]);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const [templatesRes, settingsRes] = await Promise.all([
        axios.get('/outreach/templates'),
        axios.get('/outreach/settings'),
      ]);

      if (templatesRes.data.success) {
        setTemplates(templatesRes.data.data);
      }
      if (settingsRes.data.success) {
        setLogoUrl(settingsRes.data.data.logo_url);
      }
    } catch (error) {
      console.error('Failed to fetch outreach data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);

    if (templateId) {
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        setSubject(template.subject);
        setMessage(template.body);
      }
    } else {
      setSubject('');
      setMessage('');
    }
  };

  const handleSend = async () => {
    if (!subject || !message) {
      alert('Please provide a subject and message');
      return;
    }

    try {
      setIsSending(true);
      const prospectIds = selectedProspects.map((p) => p.id);

      const response = await axios.post('/outreach/send', {
        prospectIds,
        prospects: selectedProspects.map((p) => ({
          id: p.id,
          shared_item_id: p.shared_item_id,
          name: p.name || p.full_name,
          first_name: p.first_name,
          last_name: p.last_name,
          company_name: p.company_name || p.company,
          email: p.email,
          emails: p.emails || p.email_addresses,
          phone: p.phone_number || p.phone,
        })),
        subject,
        message,
        templateId: selectedTemplateId,
        senderName,
        senderEmail,
      });

      if (response.data.success) {
        const result = response.data.data;
        if (result.failed > 0 && result.sent === 0) {
          alert(`Failed to send emails:\n${result.errors.join('\n')}`);
        } else if (result.failed > 0) {
          alert(
            `Sent ${result.sent} emails successfully, but ${result.failed} failed:\n${result.errors.join('\n')}`
          );
          if (onSent) onSent(result);
          onClose();
        } else {
          if (onSent) onSent(result);
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to send outreach emails:', error);
      const errMsg = error.response?.data?.error || 'Failed to send emails. Please try again.';
      alert(errMsg);
    } finally {
      setIsSending(false);
    }
  };

  const renderPreview = () => {
    const firstProspect = selectedProspects[0] || {
      name: 'Prospect Name',
      company_name: 'Prospect Company',
    };
    const firstName = firstProspect.name ? firstProspect.name.split(' ')[0] : 'there';
    const companyName = firstProspect.company_name || 'your company';

    let previewMsg = message
      .replace(/{{firstName}}/g, firstName)
      .replace(/{{companyName}}/g, companyName)
      .replace(/{{senderName}}/g, senderName || 'Your Name');

    return (
      <div className="email-preview-paper">
        {showLogoSection && (
          <div className="preview-logo-container">
            {logoUrl ? (
              <img src={logoUrl} alt="Company Logo" className="preview-logo" />
            ) : (
              <div className="placeholder-logo" style={{ color: '#cbd5e1', fontSize: '12px' }}>
                [Company Logo Not Set]
              </div>
            )}
          </div>
        )}
        <div className="preview-content">{previewMsg}</div>
        <div className="preview-footer">
          <p>Sent via AI LeadGen Outreach System</p>
          <p>
            Regards, <br />
            <strong>{senderName || 'Your Name'}</strong>
          </p>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="outreach-modal-overlay">
      <div className="outreach-modal-content">
        <div className="outreach-modal-header">
          <h2>Compose Outreach Email</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="outreach-modal-body">
          <div className="composer-section">
            <div className="form-row">
              <div className="form-group flex-1">
                <label>Sender Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Your Name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                />
              </div>
              <div className="form-group flex-1">
                <label>Sender Email (for replies)</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="your@email.com"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Select Template</label>
              <select
                className="form-select"
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                disabled={isLoading}
              >
                <option value="">Blank Template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Subject Line</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Message Content</label>
              <textarea
                className="form-textarea"
                placeholder="Write your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="variables-hint">
                Use variables:
                <span className="variable-tag">{'{{firstName}}'}</span>
                <span className="variable-tag">{'{{companyName}}'}</span>
                <span className="variable-tag">{'{{senderName}}'}</span>
              </div>
            </div>
          </div>

          <div className="preview-section">
            <div className="preview-header-label">Email Preview</div>
            <div className="email-preview-container">{renderPreview()}</div>
          </div>
        </div>

        <div className="outreach-modal-footer">
          <div className="recipient-info">
            <div className="recipient-header">
              Recipient(s): <strong>{selectedProspects.length}</strong> selected
            </div>
            <div className="recipient-list">
              {selectedProspects.map((p) => (
                <span key={p.id} className="recipient-tag" title={p.email || p.company_name}>
                  {p.name}
                </span>
              ))}
            </div>
          </div>
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose} disabled={isSending}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSend}
              disabled={isSending || !subject || !message}
            >
              {isSending ? (
                <>
                  <span className="loading-spinner"></span>
                  Sending...
                </>
              ) : (
                <>Send Outreach Emails</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposerModal;
